// AuthContext for global authentication state
import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const refreshIntervalRef = useRef(null);
  const lastResumeRefreshRef = useRef(0);

  const startRefreshInterval = useCallback(() => {
    if (refreshIntervalRef.current) clearInterval(refreshIntervalRef.current);
    // Refresh every 6 hours
    refreshIntervalRef.current = setInterval(async () => {
      try {
        const res = await api.post('/auth/refresh');
        if (res.data.token) {
          localStorage.setItem('token', res.data.token);
        }
      } catch {
        // Token invalid — force logout
        localStorage.removeItem('token');
        setUser(null);
      }
    }, 6 * 60 * 60 * 1000);
  }, []);

  const stopRefreshInterval = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get('/auth/me');
      if (res.data.token) {
        localStorage.setItem('token', res.data.token);
      }
      setUser(res.data);
      return true;
    } catch {
      return false;
    }
  }, []);

  useEffect(() => {
    // Try to fetch current user on mount
    refreshUser()
      .then(sessionRestored => {
        if (sessionRestored) startRefreshInterval();
        else setUser(null);
      })
      .finally(() => setLoading(false));

    return () => stopRefreshInterval();
  }, [refreshUser, startRefreshInterval, stopRefreshInterval]);

  useEffect(() => {
    const handleUnauthorized = () => {
      stopRefreshInterval();
      setUser(null);
    };

    const refreshSessionAfterResume = () => {
      if (!user || !localStorage.getItem('token')) return;
      const now = Date.now();
      if (now - lastResumeRefreshRef.current < 60 * 1000) return;
      lastResumeRefreshRef.current = now;
      refreshUser();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) refreshSessionAfterResume();
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    window.addEventListener('pageshow', refreshSessionAfterResume);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener('pageshow', refreshSessionAfterResume);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [refreshUser, stopRefreshInterval, user]);

  const login = async (email, password, remember = false) => {
    const res = await api.post('/auth/login', { email, password, remember });
    localStorage.setItem('token', res.data.token);
    setUser(res.data.user);
    startRefreshInterval();
    return res.data.user;
  };

  const register = async (data) => {
    const res = await api.post('/auth/register', data);
    
    // Check if user needs admin approval (no token returned)
    if (res.data.pendingApproval) {
      return { pendingApproval: true, message: res.data.message };
    }
    
    // First user (auto-approved) gets immediate login
    localStorage.setItem('token', res.data.token);
    setUser(res.data.user);
    startRefreshInterval();
    return res.data.user;
  };

  const logout = async () => {
    stopRefreshInterval();
    localStorage.removeItem('token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
